import type { CreateBridgeConnectionOptions } from './bridge-connection-types.js';
import type { BridgeConnectionRuntime } from './bridge-connection-runtime-types.js';
import { createBridgeAccessAndAcp } from './create-bridge-access-and-acp.js';
import { createBridgePreviewStack } from './create-bridge-preview-stack.js';
import { createBridgeRuntimeMessageSetup } from './create-bridge-runtime-message-setup.js';
import { createBridgeWorktreeManagers } from './create-bridge-worktree-managers.js';
import { createLocalShortcut } from '../local-shortcut/lib/create.js';
import type { MainBridgeConnectionState } from './create-main-bridge-reconnect-state.js';
import type { LogFn } from '../log.js';

export type { BridgeConnectionRuntime } from './bridge-connection-runtime-types.js';

export async function createBridgeConnectionRuntime(
  options: CreateBridgeConnectionOptions,
  params: {
    state: MainBridgeConnectionState;
    getWs: () => WebSocket | null;
    logFn: LogFn;
  },
): Promise<BridgeConnectionRuntime> {
  const { apiUrl, workspaceId } = options;
  const firehoseServerUrl = options.firehoseServerUrl ?? options.proxyServerUrl;
  const { state, getWs, logFn } = params;

  const tokens = {
    accessToken: options.authToken,
    refreshToken: options.refreshToken,
  };

  const { worktreesRootPath, sessionWorktreeManager, previewWorktreeManager } =
    createBridgeWorktreeManagers({ worktreesRootPath: options.worktreesRootPath, log: logFn });

  const { e2ee, acpManager, bridgeAccessPromise } = await createBridgeAccessAndAcp({
    apiUrl,
    workspaceId,
    tokens,
    e2eCertificate: options.e2eCertificate,
    getWs,
    log: logFn,
  });

  const localShortcut = await createLocalShortcut({
    workspaceId,
    sessionWorktreeManager,
    e2ee,
  });

  logFn('CLI running. Press Ctrl+C to exit.');

  const { previewEnvironmentManager, scheduleInitialIndexBuildsOnce, identifyReportedPaths } =
    createBridgePreviewStack({
      getWs,
      log: logFn,
      e2ee,
      previewWorktreeManager,
      worktreesRootPath,
    });
  Object.assign(identifyReportedPaths, localShortcut.identify);

  const { bridgeHeartbeat, messageDeps } = createBridgeRuntimeMessageSetup({
    apiUrl,
    workspaceId,
    firehoseServerUrl,
    state,
    getWs,
    log: logFn,
    tokens,
    acpManager,
    sessionWorktreeManager,
    previewWorktreeManager,
    previewEnvironmentManager,
    e2ee,
    identifyReportedPaths,
  });

  return {
    state,
    getWs,
    logFn,
    tokens,
    acpManager,
    bridgeAccessPromise,
    previewEnvironmentManager,
    bridgeHeartbeat,
    messageDeps,
    identifyReportedPaths,
    scheduleInitialIndexBuildsOnce,
    e2ee,
    localShortcutPromise: Promise.resolve(localShortcut.handle),
  };
}
